// ============================================================================
// SAMPLE CATEGORY SERVICE (Reference Implementation)
// ============================================================================
// API calls for Sample Category module

import { get, post, put, del } from '../client/axiosClient';
import { sampleCategoryEndpoints } from '../endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import type {
  ApiResponse,
  BackendPagedResult,
  PaginatedResponse,
  EmptyResponse,
  ID,
  SampleCategoryDto,
  CreateSampleCategoryRequest,
  UpdateSampleCategoryRequest,
  SampleCategoryQueryParams,
} from '../types';

class SampleCategoryService {
  /** Get paginated list of categories */
  async getAll(params?: SampleCategoryQueryParams): Promise<PaginatedResponse<SampleCategoryDto>> {
    const endpoint = params
      ? buildEndpointWithQuery(sampleCategoryEndpoints.list(), params)
      : sampleCategoryEndpoints.list();
    const response = await get<ApiResponse<BackendPagedResult<SampleCategoryDto>>>(endpoint);
    const paged = response.data.data;
    return {
      success: response.data.success,
      message: response.data.message,
      data: Array.from(paged.items || []),
      pagination: {
        currentPage: paged.page,
        pageSize: paged.pageSize,
        totalPages: paged.totalPages,
        totalItems: paged.totalCount,
        hasNextPage: paged.hasNext,
        hasPreviousPage: paged.hasPrevious,
      },
    };
  }

  /** Get single category by ID */
  async getById(id: ID): Promise<ApiResponse<SampleCategoryDto>> {
    const response = await get<ApiResponse<SampleCategoryDto>>(
      sampleCategoryEndpoints.getById(id)
    );
    return response.data;
  }

  /** Create new category */
  async create(data: CreateSampleCategoryRequest): Promise<ApiResponse<SampleCategoryDto>> {
    const response = await post<ApiResponse<SampleCategoryDto>>(
      sampleCategoryEndpoints.create(),
      data
    );
    return response.data;
  }

  /** Update existing category */
  async update(id: ID, data: UpdateSampleCategoryRequest): Promise<ApiResponse<SampleCategoryDto>> {
    const response = await put<ApiResponse<SampleCategoryDto>>(
      sampleCategoryEndpoints.update(id),
      data
    );
    return response.data;
  }

  /** Soft-delete category */
  async delete(id: ID): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(sampleCategoryEndpoints.delete(id));
    return response.data;
  }

  /** Check whether a code is available (true = available) */
  async isCodeAvailable(code: string, excludeId?: ID): Promise<boolean> {
    const endpoint = sampleCategoryEndpoints.validateCode(code);
    const url = excludeId
      ? buildEndpointWithQuery(endpoint, { excludeId })
      : endpoint;
    const response = await get<ApiResponse<boolean>>(url);
    return response.data.data;
  }
}

export const sampleCategoryService = new SampleCategoryService();
export default sampleCategoryService;
